import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Footer.scss';

const INFO_ROUTES = ['/terms', '/privacy', '/shipping', '/imprint', '/brand'];

/**
 * Footer Behaviour
 * ----------------
 * 1. Detect whether the current document is scrollable (content taller than viewport).
 * 2. If the page IS scrollable → footer is a regular positioned element at the bottom
 *    that disappears immediately when scrolling back up
 * 3. If the page is NOT scrollable (full-viewport designs like splash / garment)
 *    → footer is hidden by default but appears while the pointer is within
 *    80 px of the bottom edge, disappearing when the pointer moves away.
 *
 * This gives a consistent experience across all templates with zero
 * hard-coded route checks and no reliance on brittle height calculations.
 */

const Footer = () => {
  const location = useLocation();
  const shouldAlwaysShowFooter = INFO_ROUTES.includes(location.pathname);
  const [isVisible, setIsVisible] = useState(shouldAlwaysShowFooter);
  const [isScrollable, setIsScrollable] = useState(false);
  const [scrollDirection, setScrollDirection] = useState('down');
  const [lastScrollY, setLastScrollY] = useState(0);
  const overscrollAccumulatorRef = useRef(0);

  /* --------------------------------------------------
   *  Detect scrollability (rerun on route change / resize)
   * -------------------------------------------------- */
  useEffect(() => {
    const updateScrollable = () => {
      const scrollable =
        document.documentElement.scrollHeight > window.innerHeight + 10; // 10 px buffer
      setIsScrollable(scrollable);
      if (!scrollable) {
        // Non-scroll pages start hidden unless forced visible
        setIsVisible(shouldAlwaysShowFooter);
      } else {
        // Scrollable pages: show immediately if forced, else hidden until overscroll
        setIsVisible(shouldAlwaysShowFooter);
      }
    };

    updateScrollable();
    window.addEventListener('resize', updateScrollable);
    return () => window.removeEventListener('resize', updateScrollable);
  }, [location.pathname, shouldAlwaysShowFooter]);

  /* --------------------------------------------------
   *  Scroll direction detection for scrollable pages
   *  DISABLED - Footer only appears via overscroll gesture
   * -------------------------------------------------- */
  useEffect(() => {
    if (!isScrollable || shouldAlwaysShowFooter) return;

    // Keep footer hidden during normal scrolling
    // Only overscroll gesture (wheel event below) will show it
    setIsVisible(false);

  }, [isScrollable, location.pathname, shouldAlwaysShowFooter]);

  /* --------------------------------------------------
   *  Overscroll gesture detection for scrollable pages
   *  Footer appears when user tries to scroll past bottom
   * -------------------------------------------------- */
  useEffect(() => {
    if (shouldAlwaysShowFooter) {
      setIsVisible(true);
      return;
    }

    if (!isScrollable) {
      // Non-scrollable pages: use wheel event
      const onWheel = (e) => {
        if (e.deltaY > 10) {
          setIsVisible(true);
        } else if (e.deltaY < -10) {
          setIsVisible(false);
        }
      };
      window.addEventListener('wheel', onWheel, { passive: true });
      return () => window.removeEventListener('wheel', onWheel);
    }

    // Scrollable pages: detect overscroll attempts at bottom
    const OVERSCROLL_THRESHOLD = 180; // Require 180px of accumulated overscroll attempts

    const handleWheel = (e) => {
      let scrollHeight, clientHeight, currentScrollY;

      // Check if we're on a collections page
      const isCollectionsPage = location.pathname !== '/' &&
                                 !location.pathname.startsWith('/admin') &&
                                 !location.pathname.startsWith('/about/cv') &&
                                 !INFO_ROUTES.includes(location.pathname) &&
                                 document.querySelector('.collection-carousels-container');

      if (isCollectionsPage) {
        const scrollContainer = document.querySelector('.collection-carousels-container');
        if (!scrollContainer) return;
        currentScrollY = scrollContainer.scrollTop;
        scrollHeight = scrollContainer.scrollHeight;
        clientHeight = scrollContainer.clientHeight;
      } else {
        currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
        scrollHeight = document.documentElement.scrollHeight;
        clientHeight = window.innerHeight;
      }

      // Check if truly at the absolute bottom (no tolerance)
      const isAtAbsoluteBottom = currentScrollY + clientHeight >= scrollHeight - 2;
      const isScrollingDown = e.deltaY > 0;

      // Only accumulate when at the absolute bottom
      if (isAtAbsoluteBottom && isScrollingDown) {
        overscrollAccumulatorRef.current += Math.abs(e.deltaY);
        if (overscrollAccumulatorRef.current >= OVERSCROLL_THRESHOLD) {
          setIsVisible(true);
          overscrollAccumulatorRef.current = OVERSCROLL_THRESHOLD; // Cap at threshold
        }
      } else if (!isAtAbsoluteBottom || !isScrollingDown) {
        // Reset accumulator when not at bottom or scrolling up
        overscrollAccumulatorRef.current = 0;
        setIsVisible(false);
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [isScrollable, location.pathname, shouldAlwaysShowFooter]);

  /* --------------------------------------------------
   *  Render
   * -------------------------------------------------- */
  // For INFO_ROUTES (terms, privacy, shipping, imprint), use overlay-footer like about/collection pages
  // For other pages, use scrollable-footer if scrollable, otherwise overlay-footer
  const footerType = shouldAlwaysShowFooter ? 'overlay-footer' : (isScrollable ? 'scrollable-footer' : 'overlay-footer');
  const footerClasses = `site-footer ${footerType} ${isVisible ? 'visible' : ''} ${scrollDirection === 'up' ? 'scrolling-up' : 'scrolling-down'}`;

  return (
    <footer className={footerClasses}>
      {!isScrollable && <div className="overlay-backdrop" />}
      <div className="footer-content">
        <div className="footer-links">
          <Link to="/brand">Brand</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/imprint">Imprint</Link>
          <a href="https://www.instagram.com/elodiecarstensen/" target="_blank" rel="noopener noreferrer" className="external-link">
            Instagram
            <span className="link-arrow">↗</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
